import { useState, useEffect, useRef, useMemo, forwardRef } from 'react';
import type { ModelAction } from '../../../../../types/responses/ModelAction';
import clsx from 'clsx';
import styles from '../PdfRenderer.module.scss';
import { services } from '../../../../../services';
import { DownloadLink } from '../../../../shared/DownloadLink';
import type { PDFNoteComment } from '../../../../../pages/notes/models/PDFNoteComment';
import type { PDFNoteCommentQuery } from '../../../../../pages/notes/models/PDFNoteCommentQuery';
import type { Highlight, HighlightDetail, TextHighlight, ViewerMode, ContractorNotePermissions } from '../types';
import { EditorComponent } from '../../../../EditorComponent';
import { stripHtml, truncate, formatTime, typeIcon, typeLabel } from '../helpers/formatting';
import { NoteActions } from './NoteActions';
import { noteColorFor } from '../helpers/noteColor';
import { isOwnHighlight } from '../helpers/isOwnHighlight';
import { PDFNoteCode } from '../../../../../pages/notes/models/PDFNoteCode';
import { PDFNoteCodeTypeVals } from '../../../../../helpers/mapping/PDFNoteCodeTypeVals';
import { formatPDFNoteCodeLabel } from '../../../../../helpers/mapping/formatPDFNoteCodeLabel';
import { CommentActionModal } from './CommentActionModal';
import type { PendingActionType } from './CommentActionModal';
import { CustomDropdown } from './CustomDropdown';
import type { DropdownOption } from './CustomDropdown';
import { ImageLightbox } from './ImageLightbox';

export interface CommentsPanelProps {
    noteId: number;
    /** Актуальный TotalCount после загрузки/отправки — для счётчика в шапке карточки. */
    onCommentsChanged?: (count: number) => void;
}

/** Сколько замечаний показывать на одной странице списка */
const REMARKS_PAGE_SIZE = 10;

/**
 * Подпись кода нарушения для пилюли на карточке — «2 - Нарушения нормативных
 * требований». Возвращает null, если код не задан или справочник ещё не приехал.
 *
 * Цвет пилюли к содержимому справочника не привязан: в макете три кода
 * раскрашены в три цвета, но реальный справочник (PDFNoteCodeConfig.cs) шире
 * и правится миграциями — жёсткая карта id→цвет разъехалась бы молча.
 */
function resolveCodeLabel(
    codeId: number | undefined,
    codes: PDFNoteCode[] | undefined,
    isExecutive?: boolean,
): string | null {
    if (codeId == null || !codes?.length) return null;
    const code = codes.find(c => c.Id === codeId);
    if (!code) return null;
    return formatPDFNoteCodeLabel(
        code,
        isExecutive ? PDFNoteCodeTypeVals.Executive : PDFNoteCodeTypeVals.Ordinary,
    );
}

interface Props {
    mode: ViewerMode;
    // Панель комментариев внедряется приложением, а не гейтится флагом:
    // фильтрация (NotForContractor) и наличие эндпоинта на запись реально
    // различаются между pda-front и pda-cfront, общий код это не исправит.
    // null — не показывать комментарии вовсе.
    commentsPanel?: React.ComponentType<CommentsPanelProps> | null;
    contractorPermissions?: Record<number, ContractorNotePermissions>;
    onContractorComplete?: (noteId: number, comment: string) => Promise<void>;
    onContractorCancelCompletion?: (noteId: number) => Promise<void>;
    noteLinkHref?: (noteId: number) => string | null;
    /** Обновить счётчик комментариев в шапке карточки (локальный highlights-state). */
    onCommentsCountChange?: (noteId: number, count: number) => void;
    highlights: Highlight[];
    selectedId: string | null;
    /** Открытая страница документа — для фильтра «Эта страница». Word: всегда 1. */
    currentPage?: number;
    collapsed?: boolean;
    onSelect: (h: Highlight) => void;
    onSaveNote: (id: string, note: string, isCritical?: boolean, codeId?: number) => void;
    onDelete: (id: string) => void;
    /** Повторная отправка локальной заметки после ошибки сохранения. */
    onRetryPersist?: (id: string) => void;
    onDeleteNote?: (noteId: number) => Promise<void>;
    onToggle?: () => void;
    style?: React.CSSProperties;
    sidebarRef?: React.Ref<HTMLDivElement>;
    noteActions?: ModelAction[];
    onApproveNote?: (noteId: number) => Promise<void>;
    onCancelNote?: (noteId: number, comment: string) => Promise<void>;
    onReturnToExpert?: (noteId: number, comment: string) => Promise<void>;
    onSendForApproval?: (noteId: number) => Promise<void>;
    onRevokeDecision?: (noteId: number) => Promise<void>;
    onConfirmCompletion?: (noteId: number) => Promise<void>;
    onConfirmNotCompleted?: (noteId: number, comment: string) => Promise<void>;
    onCancelByInitiator?: (noteId: number, comment: string) => Promise<void>;
    onCancelCompletion?: (noteId: number) => Promise<void>;
    onCompleteCompletedGIP?: (noteId: number, comment: string) => Promise<void>;
    onRequestAssignToExpert?: (noteId: number) => void;
    codes?: PDFNoteCode[];
    isExecutive?: boolean;
    showCriticalCode?: boolean;
}

/** Инициалы для кружка-аватара комментария: «GIP User» -> «GU». */
function initials(name: string): string {
    const parts = name.trim().split(/\s+/).filter(Boolean);
    if (parts.length === 0) return '?';
    if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
    return (parts[0][0] + parts[1][0]).toUpperCase();
}

function escapeHtml(text: string): string {
    const div = document.createElement('div');
    div.appendChild(document.createTextNode(text));
    return div.innerHTML;
}

/**
 * Встроенная панель комментариев. Экспортируется, чтобы приложение могло
 * подставить её в `commentsPanel` явно (см. комментарий у пропа): гейт по
 * `mode` — это fail-safe для pda-cfront, а не запрет на комментарии в review.
 *
 * `readOnly` убирает форму отправки, оставляя список: в залоченной задаче
 * (mode='review') переписку нужно читать, но не дописывать.
 */
export function SidebarComments({ noteId, readOnly, onCommentsChanged }: CommentsPanelProps & { readOnly?: boolean }) {
    const [comments, setComments] = useState<PDFNoteComment[]>([]);
    const [loading, setLoading] = useState(false);
    const [commentText, setCommentText] = useState('');
    const [sending, setSending] = useState(false);
    const textareaRef = useRef<HTMLTextAreaElement>(null);
    const onCommentsChangedRef = useRef(onCommentsChanged);
    onCommentsChangedRef.current = onCommentsChanged;

    const loadComments = () => {
        setLoading(true);
        services.pDFNoteCommentService
            .fetchPaged({ NoteIds: [noteId], Take: 50, Skip: 0 } as PDFNoteCommentQuery)
            .then(res => {
                const items = res.data.Items ?? [];
                setComments(items);
                onCommentsChangedRef.current?.(res.data.TotalCount ?? items.length);
            })
            .finally(() => setLoading(false));
    };

    useEffect(() => {
        if (noteId > 0) loadComments();
    }, [noteId]);

    const handleSend = () => {
        const text = commentText.trim();
        if (!text || sending) return;
        setSending(true);
        services.pDFNoteCommentService
            .save([{
                Title: `<p>${escapeHtml(text)}</p>`,
                NoteId: noteId,
                NotForContractor: false,
                WorkflowInstanceId: 0,
            } as PDFNoteComment])
            .then(() => {
                setCommentText('');
                loadComments();
            })
            .finally(() => setSending(false));
    };

    return (
        <div className={styles.sidebarComments}>
            <div className={styles.sidebarCommentsTitle}>
                Комментарии
                {comments.length > 0 && <span>{comments.length}</span>}
            </div>

            {loading ? (
                <div className={styles.sidebarCommentsLoading}>
                    <i className="pi pi-spin pi-spinner" />
                </div>
            ) : comments.length === 0 ? (
                <div className={styles.sidebarCommentsEmpty}>Нет комментариев</div>
            ) : (
                <div className={styles.sidebarCommentsList}>
                    {comments.map(c => {
                        const author = (c.CreatedBy as any)?.Title ?? '';
                        return (
                            <div key={c.Id} className={styles.sidebarComment}>
                                <span className={styles.sidebarCommentAvatar}>{initials(author)}</span>
                                <div className={styles.sidebarCommentBubble}>
                                    <div className={styles.sidebarCommentMeta}>
                                        <span className={styles.sidebarCommentAuthor}>{author}</span>
                                        <span className={styles.sidebarCommentDate}>
                                            {c.Created ? formatTime(String(c.Created)) : ''}
                                        </span>
                                    </div>
                                    <div
                                        className={styles.sidebarCommentBody}
                                        dangerouslySetInnerHTML={{ __html: c.Title ?? '' }}
                                    />
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}

            {!readOnly && (
                <>
                    <div className={styles.sidebarCommentInputRow}>
                        <textarea
                            ref={textareaRef}
                            className={styles.sidebarCommentTextarea}
                            value={commentText}
                            onChange={e => setCommentText(e.target.value)}
                            placeholder="Написать комментарий..."
                            rows={2}
                            onKeyDown={e => {
                                if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) handleSend();
                            }}
                        />
                        <button
                            type="button"
                            className={styles.sidebarCommentSendBtn}
                            disabled={!commentText.trim() || sending}
                            onClick={handleSend}
                            title="Отправить (Ctrl+Enter)"
                        >
                            {sending
                                ? <i className="pi pi-spin pi-spinner" />
                                : <i className="fa-solid fa-paper-plane" />}
                        </button>
                    </div>
                    <div className={styles.sidebarCommentHint}>Ctrl+Enter для отправки</div>
                </>
            )}
        </div>
    );
}

function SidebarExpanded({ h, group, detail, onDelete, onSelectGeo,
    isEditing, editingNote, onEditNoteChange, onSaveEdit, onCancelEdit,
    noteActions,
    editingIsCritical, onEditingIsCriticalChange, editingCodeId, onEditingCodeIdChange,
    codes, isExecutive, showCriticalCode,
    mode, commentsPanel, onCommentsCountChange,
}: {
    mode: ViewerMode;
    commentsPanel?: React.ComponentType<CommentsPanelProps> | null;
    onCommentsCountChange?: (noteId: number, count: number) => void;
    h: Highlight;
    group: Highlight[];
    detail: HighlightDetail;
    onDelete: (id: string) => void;
    onSelectGeo: (h: Highlight) => void;
    isEditing: boolean;
    editingNote: string;
    onEditNoteChange: (val: string) => void;
    onSaveEdit: () => void;
    onCancelEdit: () => void;
    noteActions?: ModelAction[];
    editingIsCritical: boolean;
    onEditingIsCriticalChange: (val: boolean) => void;
    editingCodeId?: number;
    onEditingCodeIdChange: (val: number | undefined) => void;
    codes?: PDFNoteCode[];
    isExecutive?: boolean;
    showCriticalCode?: boolean;
}) {
    const panelRef = useRef<HTMLDivElement>(null);
    // Картинки приходят внутри titleHtml, поэтому лайтбокс открываем делегированием
    // клика по <img>, а не пропсом на каждую картинку.
    const [lightboxSrc, setLightboxSrc] = useState<string | null>(null);

    const noteId = detail.noteId ?? 0;
    // В review-режиме редактирование недоступно структурно, независимо от прав с бэкенда.
    const canEdit = mode === 'edit' && (!noteActions || noteActions.some(a => a.Id === 207 && a.ObjectId === noteId));
    const CommentsPanel = commentsPanel;
    const codeRequired = showCriticalCode !== false;
    const missingCode = codeRequired && !editingCodeId;

    useEffect(() => {
        panelRef.current?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    }, []);

    return (
        <div ref={panelRef} className={styles.sidebarExpanded}>
            {isEditing && (
                <div className={styles.sidebarExpandedFooter}>
                    <button
                        type="button"
                        className={styles.sidebarEditCancelBtn}
                        onClick={e => { e.stopPropagation(); onCancelEdit(); }}
                    >
                        Отмена
                    </button>
                    <button
                        type="button"
                        className={styles.sidebarEditSaveBtn}
                        disabled={missingCode}
                        onClick={e => { e.stopPropagation(); onSaveEdit(); }}
                    >
                        <i className="fa-solid fa-floppy-disk" style={{ marginRight: 4 }} />
                        Сохранить
                    </button>
                </div>
            )}

            {isEditing ? (
                <>
                    {showCriticalCode !== false && (
                        <div className={styles.sidebarEditCriticalRow}>
                            <label className={styles.sidebarEditCheckboxLabel}>
                                <input
                                    type="checkbox"
                                    checked={editingIsCritical}
                                    onChange={e => onEditingIsCriticalChange(e.target.checked)}
                                />
                                Критическое
                            </label>
                            <select
                                className={styles.sidebarEditCodeSelect}
                                value={editingCodeId ?? ''}
                                onChange={e => onEditingCodeIdChange(e.target.value ? Number(e.target.value) : undefined)}
                            >
                                <option value="">Выберите код</option>
                                {(codes ?? []).map(c => (
                                    <option key={c.Id} value={c.Id}>
                                        {formatPDFNoteCodeLabel(c, isExecutive ? PDFNoteCodeTypeVals.Executive : PDFNoteCodeTypeVals.Ordinary)}
                                    </option>
                                ))}
                            </select>
                        </div>
                    )}
                    <EditorComponent
                        value={editingNote}
                        onTextChange={e => onEditNoteChange(e.htmlValue ?? '')}
                        style={{ minHeight: '100px', height: '220px' }}
                        onClick={(e: React.MouseEvent) => e.stopPropagation()}
                    />
                </>
            ) : detail.titleHtml && (
                <div
                    className={styles.sidebarNoteHtml}
                    onClick={e => {
                        const el = e.target as HTMLElement;
                        if (el.tagName !== 'IMG') return;
                        e.stopPropagation();
                        setLightboxSrc((el as HTMLImageElement).src);
                    }}
                    dangerouslySetInnerHTML={{ __html: detail.titleHtml }}
                />
            )}

            {lightboxSrc && (
                <ImageLightbox src={lightboxSrc} onClose={() => setLightboxSrc(null)} />
            )}

            {(detail.attachments?.length ?? 0) > 0 && (
                <div className={styles.sidebarAttachments}>
                    {detail.attachments!.map(a => (
                        <DownloadLink
                            key={a.id}
                            path={`Attachments/Download?id=${a.attachmentId}`}
                            className={styles.sidebarAttachment}
                            onClick={e => e.stopPropagation()}
                        >
                            <i className="fas fa-paperclip" />
                            {a.title}
                        </DownloadLink>
                    ))}
                </div>
            )}

            {detail.contractorComment && (
                <div className={styles.sidebarContractorBlock}>
                    <div className={styles.sidebarContractorHeader}>
                        <span>{detail.contractorCommentLabel ?? 'Ответ КА'}</span>
                        {detail.contractorCommentDate && (
                            <span>{formatTime(detail.contractorCommentDate)}</span>
                        )}
                    </div>
                    <div dangerouslySetInnerHTML={{ __html: detail.contractorComment }} />
                </div>
            )}

            {(detail.tags?.length ?? 0) > 0 && (
                <div className={styles.sidebarTagsRow}>
                    {detail.tags!.map(t => (
                        <span key={t.title} className={clsx(t.badgeClass, styles.sidebarBadge)}>
                            {t.title}
                        </span>
                    ))}
                </div>
            )}

            {/* Список геометрий - навигация к каждой + удаление.
                Для заметки без геометрии (type: 'none') показывать нечего */}
            {!(group.length === 1 && group[0].type === 'none') && (
            <div className={styles.sidebarGeometries}>
                {group.map(geo => (
                    <div key={geo.id} className={styles.sidebarGeometryItem}>
                        <button
                            type="button"
                            className={styles.sidebarGeometryNav}
                            onClick={e => { e.stopPropagation(); onSelectGeo(geo); }}
                            title="Перейти к выделению"
                        >
                            <i className={typeIcon(geo)} />
                            Стр. {geo.pageNumber} · {typeLabel(geo)}
                        </button>
                        {canEdit && (
                            <button
                                type="button"
                                className={clsx(styles.sidebarActionBtn, styles['sidebarActionBtn--danger'])}
                                aria-label="Удалить выделение"
                                onClick={e => { e.stopPropagation(); onDelete(geo.id); }}
                            >
                                <i className="fa-solid fa-trash" />
                            </button>
                        )}
                    </div>
                ))}
            </div>
            )}

            {/* Панель комментариев. Встроенная SidebarComments умеет писать и не
                фильтрует NotForContractor — она безопасна только в 'edit'.
                В 'review' панель обязана прийти извне; её отсутствие означает
                «без комментариев», а не «показать всё». Fail-safe by design. */}
            {detail.noteId != null && detail.noteId > 0 && (
                CommentsPanel !== undefined
                    ? (CommentsPanel !== null && (
                        <CommentsPanel
                            noteId={detail.noteId}
                            onCommentsChanged={count => onCommentsCountChange?.(detail.noteId!, count)}
                        />
                    ))
                    : (mode === 'edit' && (
                        <SidebarComments
                            noteId={detail.noteId}
                            onCommentsChanged={count => onCommentsCountChange?.(detail.noteId!, count)}
                        />
                    ))
            )}
        </div>
    );
}

// === Основной компонент ===
export const AnnotationsSidebar = forwardRef<HTMLDivElement, Props>(function AnnotationsSidebar(
    { highlights, selectedId, currentPage, collapsed, onSelect, onSaveNote, onDelete, onRetryPersist, onDeleteNote, onToggle, style, noteActions, onApproveNote, onCancelNote, onReturnToExpert, onSendForApproval, onRevokeDecision, onConfirmCompletion, onConfirmNotCompleted, onCancelByInitiator, onCancelCompletion, onCompleteCompletedGIP, onRequestAssignToExpert, codes, isExecutive, showCriticalCode,
      mode, commentsPanel, contractorPermissions, onContractorComplete, onContractorCancelCompletion, noteLinkHref, onCommentsCountChange },
    ref
) {
    const [mobileOpen, setMobileOpen] = useState(false);
    const [expandedGroupKey, setExpandedGroupKey] = useState<string | null>(null);
    const [editingGroupKey, setEditingGroupKey] = useState<string | null>(null);
    const [editingNote, setEditingNote] = useState('');
    const [editingIsCritical, setEditingIsCritical] = useState(false);
    const [editingCodeId, setEditingCodeId] = useState<number | undefined>(undefined);

    const [filterAuthor, setFilterAuthor] = useState<string>('all');
    const [filterCritical, setFilterCritical] = useState<string>('all');
    const [filterThisPage, setFilterThisPage] = useState(false);
    const [remarkPage, setRemarkPage] = useState(1);
    // Действие, ожидающее комментарий. Живёт на уровне сайдбара, а не внутри
    // NoteActions: диалог один, а открыть его может и кнопка на карточке.
    const [pendingAction, setPendingAction] = useState<{
        type: PendingActionType;
        noteId: number;
    } | null>(null);

    const confirmPendingAction = (comment: string) => {
        if (!pendingAction) return;
        const { type, noteId } = pendingAction;
        if (type === 'cancel') onCancelNote?.(noteId, comment);
        else if (type === 'return') onReturnToExpert?.(noteId, comment);
        else if (type === 'confirm-not-completed') onConfirmNotCompleted?.(noteId, comment);
        else if (type === 'cancel-by-initiator') onCancelByInitiator?.(noteId, comment);
        else if (type === 'complete') onContractorComplete?.(noteId, comment);
        else if (type === 'complete-gip') onCompleteCompletedGIP?.(noteId, comment);
        setPendingAction(null);
    };

    // Ссылка на карточку замечания. Возврат null — не рендерить
    // (в pda-cfront роута /notes/:id нет, там проп отдаёт null).
    const resolveNoteHref = noteLinkHref ?? ((id: number) => `/notes/${id}`);

    const cancelEdit = () => {
        setEditingGroupKey(null);
        setEditingNote('');
        setEditingIsCritical(false);
        setEditingCodeId(undefined);
    };

    // Уникальные авторы из highlights для dropdown
    const authorOptions = useMemo<DropdownOption[]>(() => {
        const myName = highlights.find(h => isOwnHighlight(h))?.createdBy;
        const names = new Set<string>();
        for (const h of highlights) {
            if (h.createdBy && h.createdBy !== myName) names.add(h.createdBy);
        }
        const sorted = [...names].sort((a, b) => a.localeCompare(b, 'ru'));
        return [
            { label: 'Все', value: 'all', icon: 'fa-solid fa-users' },
            { label: 'Мои', value: 'me', icon: 'fa-solid fa-user' },
            ...sorted.map(name => ({ label: name, value: name, icon: 'fa-solid fa-user' })),
        ];
    }, [highlights]);

    const criticalOptions = useMemo<DropdownOption[]>(() => [
        { label: 'Все', value: 'all' },
        { label: 'Критические', value: 'critical', icon: 'fa-solid fa-triangle-exclamation' },
        { label: 'Некритические', value: 'non-critical' },
    ], []);

    // Группируем по noteId (detail.noteId) или по h.id для несохранённых.
    // useMemo здесь обязателен: от идентичности groups зависит filteredGroups,
    // а от неё — эффект автоперехода на страницу выбранного замечания. Без мемо
    // массив пересоздавался бы каждый рендер и эффект возвращал бы список
    // на страницу выбранной заметки, не давая листать.
    const groups = useMemo(() => {
        const sorted = [...highlights].sort((a, b) => {
            if (a.pageNumber !== b.pageNumber) return a.pageNumber - b.pageNumber;
            return a.createdAt.localeCompare(b.createdAt);
        });
        const map = new Map<string, Highlight[]>();
        for (const h of sorted) {
            const key = h.detail?.noteId != null ? String(h.detail.noteId) : h.id;
            if (!map.has(key)) map.set(key, []);
            map.get(key)!.push(h);
        }
        return [...map.entries()]; // [groupKey, highlights[]]
    }, [highlights]);

    const filteredGroups = useMemo(() => {
        if (filterAuthor === 'all' && filterCritical === 'all' && !filterThisPage) return groups;
        return groups.filter(([, group]) => {
            const h = group[0];
            if (filterAuthor === 'me' && !isOwnHighlight(h)) return false;
            if (filterAuthor !== 'all' && filterAuthor !== 'me' && h.createdBy !== filterAuthor) return false;
            const critical = h.isCritical ?? h.detail?.isCritical;
            if (filterCritical === 'critical' && !critical) return false;
            if (filterCritical === 'non-critical' && critical) return false;
            // Замечание может иметь несколько геометрий на разных страницах —
            // показываем, если хотя бы одна лежит на открытой странице
            if (filterThisPage && currentPage != null && !group.some(g => g.pageNumber === currentPage)) return false;
            return true;
        });
    }, [groups, filterAuthor, filterCritical, filterThisPage, currentPage]);

    // === Пагинация списка ===
    const pageCount = Math.max(1, Math.ceil(filteredGroups.length / REMARKS_PAGE_SIZE));
    const safeRemarkPage = Math.min(remarkPage, pageCount);
    const pagedGroups = useMemo(
        () => filteredGroups.slice((safeRemarkPage - 1) * REMARKS_PAGE_SIZE, safeRemarkPage * REMARKS_PAGE_SIZE),
        [filteredGroups, safeRemarkPage]
    );

    // Смена фильтров сбрасывает на первую страницу — иначе после сужения списка
    // пользователь остаётся на несуществующей странице и видит пустоту.
    // currentPage входит в ключ только при включённом фильтре: иначе листание
    // документа сбрасывало бы позицию в списке замечаний без причины.
    const filterKey = `${filterAuthor}|${filterCritical}|${filterThisPage ? currentPage ?? '' : ''}`;
    useEffect(() => {
        setRemarkPage(1);
    }, [filterKey]);

    // Раскрываем группу при выборе замечания снаружи (клик на фигуру в просмотрщике)
    useEffect(() => {
        if (!selectedId) return;
        const h = highlights.find(hl => hl.id === selectedId);
        if (!h) return;
        const groupKey = h.detail?.noteId != null ? String(h.detail.noteId) : h.id;
        setExpandedGroupKey(groupKey);
        cancelEdit();
        // Выбранное замечание может лежать на другой странице списка — без
        // перехода initialScrollToNoteId раскрывал бы карточку вне видимости
        const index = filteredGroups.findIndex(([key]) => key === groupKey);
        if (index >= 0) setRemarkPage(Math.floor(index / REMARKS_PAGE_SIZE) + 1);
    }, [selectedId, highlights, filteredGroups]);

    const isFiltered = filterAuthor !== 'all' || filterCritical !== 'all' || filterThisPage;

    if (collapsed) {
        return (
            <div className={clsx(styles.sidebar, styles['sidebar--collapsed'])} style={style} ref={ref}>
                {onToggle && (
                    <button
                        type="button"
                        className={styles.sidebarCollapsedToggle}
                        onClick={onToggle}
                        aria-label="Развернуть панель замечаний"
                        title="Развернуть панель замечаний"
                    >
                        <i className="fa-solid fa-chevron-left" />
                    </button>
                )}
                <span className={styles.sidebarCollapsedLabel}>
                    ЗАМЕЧАНИЯ {filteredGroups.length}
                </span>
            </div>
        );
    }

    return (
        <div className={clsx(styles.sidebar, mobileOpen && styles['sidebar--mobileOpen'])} style={style} ref={ref}>
            <div className={styles.sidebarTitle}>
                ЗАМЕЧАНИЯ
                {filteredGroups.length > 0 && (
                    <span className={styles.sidebarCount}>
                        {filteredGroups.length}{isFiltered && ` из ${groups.length}`}
                    </span>
                )}
                <button
                    type="button"
                    className={styles.sidebarToggle}
                    onClick={() => setMobileOpen(v => !v)}
                    aria-label={mobileOpen ? 'Свернуть' : 'Развернуть'}
                >
                    <i className={clsx('fa-solid', mobileOpen ? 'fa-chevron-up' : 'fa-chevron-down')} />
                </button>
                {onToggle && (
                    <button
                        type="button"
                        className={styles.sidebarCollapsePin}
                        onClick={onToggle}
                        aria-label="Свернуть панель замечаний"
                        title="Свернуть панель замечаний"
                    >
                        <i className="fa-solid fa-chevron-right" />
                    </button>
                )}
            </div>

            {groups.length > 0 && (
                <div className={styles.sidebarFilters}>
                    <CustomDropdown
                        icon="fa-solid fa-user"
                        options={authorOptions}
                        value={filterAuthor}
                        onChange={setFilterAuthor}
                    />
                    <CustomDropdown
                        icon="fa-solid fa-triangle-exclamation"
                        options={criticalOptions}
                        value={filterCritical}
                        onChange={setFilterCritical}
                    />
                    {currentPage != null && (
                        <button
                            type="button"
                            className={styles.sidebarPageFilter}
                            onClick={() => setFilterThisPage(v => !v)}
                            aria-pressed={filterThisPage}
                        >
                            <span className={clsx(
                                styles.sidebarPageFilterBox,
                                filterThisPage && styles['sidebarPageFilterBox--on'],
                            )}>
                                <i className="fa-solid fa-check" />
                            </span>
                            Эта страница
                        </button>
                    )}
                </div>
            )}

            <div className={styles.sidebarBody}>
                {filteredGroups.length === 0 ? (
                    <div className={styles.sidebarEmpty}>
                        <i className="fa-regular fa-comment-dots" style={{ fontSize: 22, display: 'block', marginBottom: 8 }} />
                        {groups.length === 0 ? 'Замечаний пока нет' : 'Нет замечаний по выбранным фильтрам'}
                    </div>
                ) : (
                    pagedGroups.map(([groupKey, group]) => {
                        const h = group[0]; // представитель группы
                        const isExpanded = expandedGroupKey === groupKey;
                        const isActive = group.some(g => g.id === selectedId);
                        const isCritical = h.isCritical ?? h.detail?.isCritical;
                        const noteId = h.detail?.noteId;
                        const perms = noteId != null ? contractorPermissions?.[noteId] : undefined;
                        const codeLabel = resolveCodeLabel(h.codeId, codes, isExecutive);

                        // TODO(redesign): по макету свёрнутая карточка не показывает
                        // ни превью текста замечания, ни выделенный фрагмент, ни бейдж
                        // статуса. Код оставлен намеренно — вернуть по решению.
                        // const isText = h.type === 'text';
                        // const th = h as TextHighlight;
                        // const notePreview = h.note ? stripHtml(h.note) : '';

                        const isEditing = editingGroupKey === groupKey;

                        const handleClick = () => {
                            onSelect(h);
                            // При переключении на другую группу — сбрасываем редактирование
                            if (editingGroupKey !== null && editingGroupKey !== groupKey) cancelEdit();
                            // При сворачивании текущей группы — тоже сбрасываем
                            if (expandedGroupKey === groupKey) cancelEdit();
                            setExpandedGroupKey(prev => prev === groupKey ? null : groupKey);
                        };

                        return (
                            <div
                                key={groupKey}
                                className={clsx(
                                    styles.sidebarCard,
                                    isActive && styles['sidebarCard--active'],
                                    h.persistStatus === 'failed' && styles['sidebarCard--failed'],
                                )}
                            >
                                <div className={styles.sidebarItem} onClick={handleClick}>
                                    <div className={styles.sidebarItemBody}>
                                        <div className={styles.sidebarPage}>
                                            <i className={typeIcon(h)} />
                                            Стр. {h.pageNumber}
                                            {group.length > 1 && (
                                                <span className={styles.sidebarGeoBadge}>
                                                    +{group.length - 1}
                                                </span>
                                            )}
                                            {isCritical && (
                                                <span className={styles.sidebarCriticalPill}>
                                                    <i className="fa-solid fa-triangle-exclamation" />
                                                    Критическое
                                                </span>
                                            )}
                                            {noteId != null && noteId > 0 && (
                                                <span className={styles.sidebarCardNumber}>{noteId}</span>
                                            )}
                                            <i
                                                className={clsx(
                                                    'fa-solid',
                                                    isExpanded ? 'fa-chevron-up' : 'fa-chevron-down',
                                                    styles.sidebarChevron,
                                                )}
                                            />
                                        </div>

                                        {codeLabel && (
                                            <div className={styles.sidebarCodeRow}>
                                                <span className={clsx(
                                                    styles.sidebarCodePill,
                                                    isCritical && styles['sidebarCodePill--critical'],
                                                )}>
                                                    <span className={styles.sidebarCodeDot} />
                                                    {codeLabel}
                                                </span>
                                            </div>
                                        )}

                                        {/* TODO(redesign): скрыто по макету — выделенный текст и превью замечания.
                                        {isText && (
                                            <div className={styles.sidebarText}>
                                                <mark
                                                    className={styles.sidebarMark}
                                                    style={{ background: noteColorFor(isOwnHighlight(h)).fill }}
                                                >
                                                    {truncate(th.text, 80)}
                                                </mark>
                                            </div>
                                        )}
                                        {notePreview && (
                                            <div className={styles.sidebarNote}>
                                                {truncate(notePreview, 60)}
                                            </div>
                                        )}
                                        */}
                                        {h.detail?.statusTitle && (
                                            <div className={styles.sidebarBadgesRow} style={{ marginTop: 4 }}>
                                                <span className={clsx(h.detail.statusBadgeClass, styles.sidebarBadge)}>
                                                    {h.detail.statusTitle}
                                                </span>
                                            </div>
                                        )}

                                        <div className={styles.sidebarMeta}>
                                            <span className={styles.sidebarAuthor}>
                                                <i className="fa-solid fa-user" />
                                                {h.persistStatus === 'failed'
                                                    ? 'Не сохранено'
                                                    : h.persistStatus === 'saving'
                                                        ? 'Сохранение…'
                                                        : (h.createdBy || '—')}
                                            </span>
                                            <span className={styles.sidebarTime}>
                                                {formatTime(h.createdAt)}
                                            </span>
                                        </div>
                                    </div>
                                </div>

                                {/* Несохранённая заметка после ошибки API */}
                                {(noteId == null || noteId <= 0) && h.persistStatus === 'failed' && (
                                    <div className={styles.sidebarPersistError}>
                                        <span className={styles.sidebarPersistErrorText}>
                                            <i className="fa-solid fa-triangle-exclamation" />
                                            Не удалось сохранить замечание
                                        </span>
                                        <div className={styles.sidebarPersistErrorActions}>
                                            {onRetryPersist && (
                                                <button
                                                    type="button"
                                                    className={styles.sidebarRetryBtn}
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        onRetryPersist(h.id);
                                                    }}
                                                >
                                                    <i className="fa-solid fa-rotate-right" />
                                                    Отправить снова
                                                </button>
                                            )}
                                            <button
                                                type="button"
                                                className={styles.sidebarRetryDismiss}
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    onDelete(h.id);
                                                }}
                                            >
                                                Удалить
                                            </button>
                                        </div>
                                    </div>
                                )}

                                {(noteId == null || noteId <= 0) && h.persistStatus === 'saving' && (
                                    <div className={styles.sidebarPersistSaving}>
                                        <i className="fa-solid fa-spinner fa-spin" />
                                        Сохранение…
                                    </div>
                                )}

                                {/* Футер действий — виден без раскрытия карточки.
                                    Для несохранённого выделения (noteId нет) показывать
                                    нечего: ни счётчика комментариев, ни прав. */}
                                {noteId != null && noteId > 0 && (
                                    <div className={styles.sidebarCardActions}>
                                        {perms?.canComplete && onContractorComplete && (
                                            <button
                                                type="button"
                                                className={styles.sidebarCompleteBtn}
                                                onClick={e => {
                                                    e.stopPropagation();
                                                    setPendingAction({ type: 'complete', noteId });
                                                }}
                                            >
                                                <i className="fa-solid fa-check" />
                                                Выполнить
                                            </button>
                                        )}
                                        {!perms?.canComplete && perms?.canCancelCompletion && (
                                            <span className={styles.sidebarDonePill}>
                                                <i className="fa-solid fa-check" />
                                                Выполнено
                                            </span>
                                        )}

                                        {/* Остальные действия — иконки с тултипом, рядом
                                            с «Выполнить» и без раскрытия карточки. */}
                                        <NoteActions
                                            noteId={noteId}
                                            isOwnNote={h.detail?.isOwnNote ?? true}
                                            noteActions={noteActions}
                                            onApproveNote={onApproveNote}
                                            onCancelNote={onCancelNote}
                                            onReturnToExpert={onReturnToExpert}
                                            onSendForApproval={onSendForApproval}
                                            onRevokeDecision={onRevokeDecision}
                                            onConfirmCompletion={onConfirmCompletion}
                                            onConfirmNotCompleted={onConfirmNotCompleted}
                                            onCancelByInitiator={onCancelByInitiator}
                                            onCancelCompletion={onCancelCompletion}
                                            onRequestAssignToExpert={onRequestAssignToExpert}
                                            onDeleteNote={mode === 'edit' ? onDeleteNote : undefined}
                                            onEditStart={mode === 'edit' ? () => {
                                                // Форма редактирования живёт в раскрытой панели —
                                                // раскрываем карточку, иначе правка «уедет» в никуда.
                                                setExpandedGroupKey(groupKey);
                                                setEditingGroupKey(groupKey);
                                                setEditingNote(h.note ?? '');
                                                setEditingIsCritical(h.isCritical ?? false);
                                                setEditingCodeId(h.codeId);
                                            } : undefined}
                                            contractorPermissions={perms}
                                            onContractorCancelCompletion={onContractorCancelCompletion}
                                            onRequestCommentAction={type => setPendingAction({ type, noteId })}
                                        />

                                        <span className={styles.sidebarCardActionsRight}>
                                            <span
                                                className={styles.sidebarCommentCount}
                                                onClick={handleClick}
                                            >
                                                <i className="fa-regular fa-comments" />
                                                {h.detail?.commentsCount ?? 0}
                                            </span>
                                            {resolveNoteHref(noteId) && (
                                                <a
                                                    href={resolveNoteHref(noteId)!}
                                                    target="_blank"
                                                    rel="noreferrer"
                                                    className={styles.sidebarCardNoteLink}
                                                    title="Открыть замечание"
                                                    onClick={e => e.stopPropagation()}
                                                >
                                                    <i className="fa-solid fa-arrow-up-right-from-square" />
                                                </a>
                                            )}
                                        </span>
                                    </div>
                                )}

                                {isExpanded && h.detail && (
                                    <SidebarExpanded
                                        mode={mode}
                                        commentsPanel={commentsPanel}
                                        onCommentsCountChange={onCommentsCountChange}
                                        h={h}
                                        group={group}
                                        detail={h.detail}
                                        onDelete={onDelete}
                                        onSelectGeo={onSelect}
                                        isEditing={isEditing}
                                        editingNote={editingNote}
                                        onEditNoteChange={setEditingNote}
                                        onSaveEdit={() => {
                                            onSaveNote(h.id, editingNote, editingIsCritical, editingCodeId);
                                            cancelEdit();
                                        }}
                                        onCancelEdit={cancelEdit}
                                        editingIsCritical={editingIsCritical}
                                        onEditingIsCriticalChange={setEditingIsCritical}
                                        editingCodeId={editingCodeId}
                                        onEditingCodeIdChange={setEditingCodeId}
                                        codes={codes}
                                        isExecutive={isExecutive}
                                        showCriticalCode={showCriticalCode}
                                        noteActions={noteActions}
                                    />
                                )}
                            </div>
                        );
                    })
                )}
            </div>

            {filteredGroups.length > 0 && pageCount > 1 && (
                <div className={styles.sidebarPagination}>
                    <span className={styles.sidebarPaginationRange}>
                        {(safeRemarkPage - 1) * REMARKS_PAGE_SIZE + 1}
                        –
                        {Math.min(safeRemarkPage * REMARKS_PAGE_SIZE, filteredGroups.length)}
                        {' из '}
                        {filteredGroups.length}
                    </span>
                    <div className={styles.sidebarPaginationPages}>
                        <button
                            type="button"
                            className={styles.sidebarPaginationBtn}
                            disabled={safeRemarkPage <= 1}
                            onClick={() => setRemarkPage(p => Math.max(1, p - 1))}
                            aria-label="Предыдущая страница списка"
                        >
                            <i className="fa-solid fa-chevron-left" />
                        </button>
                        {Array.from({ length: pageCount }, (_, i) => i + 1).map(n => (
                            <button
                                key={n}
                                type="button"
                                className={clsx(
                                    styles.sidebarPaginationBtn,
                                    n === safeRemarkPage && styles['sidebarPaginationBtn--active'],
                                )}
                                onClick={() => setRemarkPage(n)}
                            >
                                {n}
                            </button>
                        ))}
                        <button
                            type="button"
                            className={styles.sidebarPaginationBtn}
                            disabled={safeRemarkPage >= pageCount}
                            onClick={() => setRemarkPage(p => Math.min(pageCount, p + 1))}
                            aria-label="Следующая страница списка"
                        >
                            <i className="fa-solid fa-chevron-right" />
                        </button>
                    </div>
                </div>
            )}

            {pendingAction && (
                <CommentActionModal
                    type={pendingAction.type}
                    noteId={pendingAction.noteId}
                    onConfirm={confirmPendingAction}
                    onCancel={() => setPendingAction(null)}
                />
            )}
        </div>
    );
});
